<template>
  <div class="so-form">
    <base-heading v-if="!isEditing">{{ $t('standingOrder.createOrderTitle') }}</base-heading>
    <base-heading v-else>{{ $t('standingOrder.edit.editTitle') }}</base-heading>

    <p class="description" v-if="!isEditing" v-html="$t('standingOrder.edit.description')"></p>
    <p class="description" v-else v-html="$t('standingOrder.edit.editDescription')"></p>

    <validation-observer tag="form"
                         ref="createEditObs"
                         @submit.prevent="submitForm">
      <div class="edit-form">
        <div class="dropdown-container">
          <template v-if="!isEditing">
            <p class="label">{{ $t('standingOrder.edit.chooseContractLabel') }}</p>
            <v-select class="select-dropdown"
                      required
                      v-model="selectedCustomer"
                      :key="selectedCustomer"
                      :options="customers"
                      :reduce="option => option.customerId"
                      :filterable="false"
                      :searchable="false"
                      label="fe_label">
            </v-select>
          </template>
          <base-input v-else
                      :value="existingCustomer"
                      id="customerIdPreselected"
                      name="customerIdPreselected"
                      class="existing-customer-field"
                      :label="$t('standingOrder.edit.chosenContractLabel')"
                      disabled
                      variant="white"/>
        </div>
        <div class="edit-input">

          <common-dropdown class="days-dropdown" chevron-visible>
            <base-v-input v-model="selectedDay"
                          disabled
                          variant="white"
                          :submitted="formSubmitted"
                          rules="required"
                          :label="$t('standingOrder.edit.dayInMonthLabel')"/>
            <div slot="content">
              <day-select-grid v-model="selectedDay"/>
            </div>
          </common-dropdown>
        </div>
        <div class="edit-input">
          <p class="label">{{ $t('standingOrder.edit.paymentCardsLabel') }}</p>
          <v-select class="select-dropdown"
                    required
                    v-model="selectedCard"
                    :options="cards"
                    :reduce="option => option.code"
                    :filterable="false"
                    :searchable="false"
                    label="fe_label">
          </v-select>
        </div>
        <div class="edit-input">
          <base-v-input v-model.trim="amount"
                        id="amount"
                        name="amount"
                        container-class="amount"
                        :submitted="formSubmitted"
                        :label="$t('standingOrder.edit.amountLabel')"
                        rules="decimal|currencyRange:2,500|max:6"
                        vid="amount"
                        variant="white"
                        maxlength="6">
            <template #input-button>
              <button v-if="!!amount" class="clear-button" type="button" @click="clearAmount">
                <img src="/assets/images/icon_close_red.svg" alt="" />
              </button>
            </template>
          </base-v-input>
        </div>
      </div>
      <div class="create-actions">
        <base-button variant="primary"
                     :disabled="$props.disabled"
                     class="action-btn"
                     type="submit">
          {{ isEditing ? $t('standingOrder.edit.editServiceLabel') : $t('standingOrder.edit.activateServiceLabel') }}
        </base-button>
        <base-button variant="secondary"
                     :disabled="$props.disabled"
                     class="action-btn"
                     @click="emitAddNewCard">
          {{ $t('standingOrder.edit.addCardLabel') }}
        </base-button>
        <base-button variant="text" class="action-btn back-btn" @click="cancelEditing">{{ $t('common.cancel') }}</base-button>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import CommonDropdown from '@/components/common/common-dropdown'
import DaySelectGrid from './day-select-grid'

export default {
  name: 'so-create-edit',
  components: { DaySelectGrid, CommonDropdown },
  data () {
    return {
      selectedCustomer: '',
      selectedDay: '',
      selectedCard: '',
      amount: '',
      formSubmitted: false,
      existingCustomer: ''
    }
  },
  computed: {
    /**
     * Flattened list of customers
     * Props that we need are nested one level too many in unnecessary subscriptionInfo object when fetched from API,
     * so here we transform that into an array of plain objects
     * Also, because the v-select is limited when it comes to displaying the selected option
     * and it cannot display combination of properties, we need to insert another property into object that contains first, last name and description
     * @returns { Array } returns array of objects
     */
    customers () {
      return this.$props.customerList.map(item => {
        item.subscriptionInfo.fe_label = `${item.subscriptionInfo.firstName} ${item.subscriptionInfo.lastName || ''} - ${item.subscriptionInfo.description}`
        return item.subscriptionInfo
      })
    },
    /**
     * Transforming array so that objects inside contain necessary labels as requested in design
     * @returns { Array }
     */
    cards () {
      return this.$props.paymentCards.map(item => {
        item.fe_label = `${this.capitalizeString(item.type)} ${this.shortCardNumber(item.cardNumber)}`
        return item
      })
    },
    isEditing () {
      return Object.keys(this.$props.selectedOrder).length > 0
    }
  },
  props: {
    selectedOrder: {
      type: Object,
      default: () => {
        return {}
      }
    },
    customerList: {
      type: Array,
      default: () => []
    },
    paymentCards: {
      type: Array,
      default: () => []
    },
    disabled: Boolean
  },
  methods: {
    capitalizeString (value) {
      if (!value) return ''
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    },
    shortCardNumber (value) {
      if (!value) return ''
      return value.slice(-8)
    },
    async submitForm () {
      const isValid = await this.$refs.createEditObs.validate()
      this.formSubmitted = true
      if (isValid) {
        if (this.isEditing) {
          const editData = {
            MtmStandingOrder: {
              card: {
                code: this.selectedCard
              },
              code: this.$props.selectedOrder.standingOrder.code,
              dateOfPayment: this.selectedDay.toString(),
              limitAmount: {
                value: this.amount
              }
            }
          }
          this.$emit('submit-edit', editData)
        } else {
          const dataToEmit = {
            customerId: this.selectedCustomer,
            cardCode: this.selectedCard,
            dayOfMonth: this.selectedDay.toString(),
            limitAmount: this.amount
          }
          this.$emit('submit-new', dataToEmit)
        }
      }
    },
    cancelEditing () {
      this.$emit('cancel-edit')
    },
    fillFieldsWithDefaultValues () {
      if (this.customers.length) this.selectedCustomer = this.customers[0].customerId
      if (this.cards.length) this.selectedCard = this.cards[0].code
    },
    fillFieldsWithExistingValues (data) {
      this.selectedCustomer = data.subscriptionInfo.customerId
      this.selectedDay = data.standingOrder.dateOfPayment
      this.selectedCard = data.standingOrder.card.code
      this.amount = data.standingOrder.limitAmount ? data.standingOrder.limitAmount.value : ''

      this.existingCustomer = `${data.subscriptionInfo.firstName} ${data.subscriptionInfo.lastName || ''} - ${data.subscriptionInfo.description}`
    },
    emitAddNewCard () {
      this.$emit('add-card')
    },
    clearAmount () {
      this.amount = ''
    }
  },
  watch: {
    selectedOrder: {
      handler (newVal) {
        if (Object.keys(newVal).length) {
          this.fillFieldsWithExistingValues(newVal)
        } else {
          this.fillFieldsWithDefaultValues()
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped lang="scss" src="./styles/so-create-edit.scss"></style>
